import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResource } from '@app/src/api/fetchHooks'
import useAssessmentQuestionnaireLink from '@app/src/hooks/useAssessmentQuestionnaireLink'
import { useAssessmentResponseItems } from '@app/src/hooks/useAssessmentResponseItems'
import type Assessment from '@app/src/types/assessment'
import type { AssessmentTemplateWithClassificationRules, ResponseItem } from '@app/src/types/resourceExplorer'
import { AssessmentType } from '@app/src/types/resourceExplorer'
import { ONE_MINUTE_MS } from '@app/src/wf-constants'
import { useMemo } from 'react'
import type { AnswerSection } from './assessmentAnswersLogic'
import { buildAnswerSections, findPrimaryResponseItem } from './assessmentAnswersLogic'

const RESPONSE_ITEM_INCLUDES = [
  'response.request',
  'files',
  'requestItem.unit.symbol',
  'requestItem.template.requestCollection',
  'requestItem.template.unit',
  'questionAnswerClassificationResults.questionAnswerClassificationRule',
  'scrapedAnswer',
]

interface UseAssessmentAnswersResult {
  sections: AnswerSection[]
  responseItems: ResponseItem[]
  primaryResponseItem: ResponseItem | undefined
  isLoading: boolean
}

export const useAssessmentAnswers = (
  assessment: Assessment,
  providerId: number | undefined,
): UseAssessmentAnswersResult => {
  const isBaseline = assessment.assessmentTemplate.assessmentType === AssessmentType.BaselineAssessment

  const { assessmentQuestionnaireLinks, isLoadingAssessmentQuestionnaireLink } = useAssessmentQuestionnaireLink({
    assessmentTemplateId: assessment.assessmentTemplateId,
    includeAssessment: false,
    includeQuestionnaire: false,
  })

  const questionnaireTemplateIds = useMemo(
    () => [...new Set(assessmentQuestionnaireLinks.map(link => link.questionnaireTemplateId))],
    [assessmentQuestionnaireLinks],
  )

  const { items: responseItems, isLoading: isLoadingResponseItems } = useAssessmentResponseItems({
    periodFilterValue: assessment.periodName,
    questionnaireTemplateIds,
    providerId,
    include: RESPONSE_ITEM_INCLUDES,
    isScreenedFilter: assessment.isScreened ? true : undefined,
    latestSubmittedOnly: !assessment.isScreened,
    enabled: questionnaireTemplateIds.length > 0,
  })

  const { data: templateWithRules, isLoading: isLoadingTemplate } =
    useFetchResource<AssessmentTemplateWithClassificationRules>({
      key: [FetchKey.AssessmentTemplateWithClassificationRules, assessment.assessmentTemplateId],
      endpoint: endpoints.assessmentTemplateWithClassificationRules(assessment.assessmentTemplateId),
      options: { enabled: !isBaseline, staleTime: ONE_MINUTE_MS },
    })

  const sections = useMemo(
    () => buildAnswerSections(assessment, responseItems, templateWithRules),
    [assessment, responseItems, templateWithRules],
  )

  return {
    sections,
    responseItems,
    primaryResponseItem: findPrimaryResponseItem(assessment, responseItems),
    isLoading: isLoadingAssessmentQuestionnaireLink || isLoadingResponseItems || isLoadingTemplate,
  }
}
